import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import heroBanner from '@/assets/hero-banner.jpg';

const tiers = [
  {
    name: 'Starter',
    tagline: 'For founders deploying their first AI agents',
    monthly: 30,
    annual: 25,
    annualSavings: '17%',
    desc: 'Ship production work with core AI agents and approval controls.',
    features: [
      'Unlimited agentic workspaces',
      'Core planner, builder, and QA agents',
      'Secure guest collaboration',
      'Email support',
    ],
    cta: 'Start Trial',
    ctaStyle: 'secondary' as const,
  },
  {
    name: 'Growth',
    tagline: 'For teams scaling agent-driven delivery',
    monthly: 89,
    annual: 69,
    annualSavings: '22%',
    desc: 'Best mix of multi-agent orchestration, governance, and delivery analytics.',
    features: [
      'Advanced multi-agent orchestration',
      'Workflow automations and task routing',
      'Priority support',
      'Usage and ROI analytics',
      'Role-based access controls',
    ],
    cta: 'Choose Growth',
    ctaStyle: 'primary' as const,
    highlighted: true,
    badge: 'Highest ROI',
  },
  {
    name: 'Scale',
    tagline: 'For enterprise agent operations',
    monthly: 149,
    annual: 119,
    annualSavings: '20%',
    desc: 'Enterprise governance for large teams running multi-project agent workloads.',
    features: [
      'Everything in Growth',
      'Dedicated success and onboarding',
      'Security and audit workflows',
      'SLA-backed support',
      'Custom procurement options',
    ],
    cta: 'Talk to Sales',
    ctaStyle: 'secondary' as const,
  },
];

const PricingPreview = () => {
  const [annual, setAnnual] = useState(true);

  return (
    <section id="pricing" className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={heroBanner} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/85 backdrop-blur-sm" />
      </div>
      <div className="mx-auto max-w-6xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Pricing for agentic AI teams
          </h2>

          {/* Toggle */}
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-border bg-card p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                !annual
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                annual
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual <span className="text-xs opacity-80">(Save up to 22%)</span>
            </button>
          </div>
        </motion.div>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {tiers.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative flex flex-col rounded-2xl p-8 ${
                t.highlighted ? 'border-2 border-primary bg-card' : 'glass-card'
              }`}
            >
              {t.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-xs font-semibold text-primary-foreground">
                  {t.badge}
                </div>
              )}

              <h3 className="text-lg font-semibold text-foreground">{t.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t.tagline}</p>

              <div className="mt-5">
                <span className="text-3xl font-bold text-foreground">
                  ${annual ? t.annual : t.monthly}
                </span>
                <span className="text-sm text-muted-foreground">per seat / month</span>
              </div>

              {annual && (
                <p className="mt-1 text-xs text-primary font-medium">
                  Save {t.annualSavings} vs monthly
                </p>
              )}

              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{t.desc}</p>

              <ul className="mt-6 flex-1 space-y-2.5">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {f}
                  </li>
                ))}
              </ul>

              {t.name === 'Scale' ? (
                <a
                  href="mailto:sales@tasking.tech?subject=Enterprise%20Inquiry"
                  className={`mt-8 inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium transition-colors ${
                    t.ctaStyle === 'primary'
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'border border-border bg-secondary text-foreground hover:border-primary/30'
                  }`}
                >
                  {t.cta}
                </a>
              ) : (
                <Link
                  to={`/login?plan=${t.name.toLowerCase()}&billing=${annual ? 'annual' : 'monthly'}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium transition-colors ${
                    t.ctaStyle === 'primary'
                      ? 'bg-primary text-primary-foreground hover:opacity-90'
                      : 'border border-border bg-secondary text-foreground hover:border-primary/30'
                  }`}
                >
                  {t.cta}
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 text-center"
        >
          <p className="text-sm text-muted-foreground">
            Need procurement support, custom terms, or large-seat pricing?{' '}
            <a
              href="mailto:sales@tasking.tech?subject=Enterprise%20Inquiry"
              className="text-primary font-medium hover:underline"
            >
              Contact enterprise sales
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingPreview;
